// @flow
import { getResponsePacket } from "api";
import map from "lodash/map";
import { useLoadResponseQueue } from "queries/response_queue/useLoadResponseQueue";
import { useUpdateResponseQueue } from "queries/response_queue/useUpdateResponseQueue";
import { getResponse } from "queries/responses/services/loadResponses";
import * as React from "react";
import { Helmet } from "react-helmet";
import { useHistory, useRouteMatch } from "react-router-dom";
import { toast } from "react-toastify";
import type { PacketResponse } from "types/packet-response";
import type { Response } from "types/response";
import type { ResponseQueue, UpdateResponseQueue } from "types/response_queue";
import Ui from "ui";
import BreadcrumbButton from "ui/atoms/BreadcrumbButton";
import Title from "ui/atoms/Title";
import Icons from "ui/Icons";
import { ContextTabletMode } from "utils/ContextTabletMode";
import { getFormattedDate } from "utils/dates";
import { buildFullName, isFilledArray } from "utils/helpers";
import { ZentakeContext } from "utils/ZentakeContext";

function TabletModeQueue(): React.Node {
  const history = useHistory();
  const { url } = useRouteMatch();
  const { setAlertConfig } = React.useContext(ZentakeContext);
  const { createFillSession, setSessions, sessions } =
    React.useContext(ContextTabletMode);
  const { data: responseQueue } = useLoadResponseQueue({
    keepPreviousData: true,
  });
  const { updateResponseQueue } = useUpdateResponseQueue();

  const onDeletePress = React.useCallback(
    (e, data: UpdateResponseQueue) => {
      e.stopPropagation();
      setAlertConfig({
        title: `Remove ${data.is_packet ? "packet" : "form"} from queue?`,
        description: `Do you really want to remove this ${
          data.is_packet ? "packet" : "form"
        } from queue?`,
        buttons: [
          { title: "Cancel", onPress: () => {} },
          {
            title: "Remove",
            style: "destructive",
            onPress: async () => {
              await updateResponseQueue(data);
            },
          },
        ],
        canDismiss: true,
      });
    },
    [setAlertConfig, updateResponseQueue],
  );

  const setPacketSession = React.useCallback(
    async (data: ResponseQueue) => {
      const packetResponse: PacketResponse = await getResponsePacket(
        data.response_uid,
        { with_response_details: true },
      );

      if (packetResponse.is_submitted) {
        toast.warning("Packet already submitted");
        await updateResponseQueue({
          response_uid: data.response_uid,
          is_queued: false,
          is_packet: data.is_packet,
        });
        return "";
      }

      let sessionId: string =
        Object.values(sessions)?.find(
          (s) => s.packetResponse?.id === packetResponse.id,
        )?.id || "";
      if (!sessionId) {
        sessionId = createFillSession({
          queue: [{ id: packetResponse.id }],
          type: data.type,
          customer: data.customer,
        });
      }

      setSessions((sessions) => ({
        [sessionId]: {
          ...sessions[sessionId],
          packetResponse,
          responses: packetResponse.responses,
          customer: packetResponse.customer,
        },
      }));

      return sessionId;
    },
    [sessions, setSessions, updateResponseQueue, createFillSession],
  );

  const setResponseSession = React.useCallback(
    async (data: ResponseQueue) => {
      const response: Response = await getResponse(data.response_uid);

      if (response.is_submitted) {
        toast.warning("Form already submitted");
        await updateResponseQueue({
          response_uid: data.response_uid,
          is_queued: false,
          is_packet: data.is_packet,
        });
        return "";
      }

      let sessionId: string =
        Object.values(sessions)?.find((s) =>
          s.responses?.find((r) => r.id === response.id),
        )?.id || "";
      if (!sessionId) {
        sessionId = createFillSession({
          queue: [{ id: response.id }],
          type: data.type,
          customer: data.customer,
        });
      }

      setSessions((sessions) => ({
        [sessionId]: {
          ...sessions[sessionId],
          responses: [response],
          customer: data.customer,
        },
      }));

      return sessionId;
    },
    [createFillSession, sessions, setSessions, updateResponseQueue],
  );

  const startSession = React.useCallback(
    async (data: ResponseQueue) => {
      let sessionId = null;
      if (data.type === "packet") {
        sessionId = await setPacketSession(data);
      } else {
        sessionId = await setResponseSession(data);
      }

      if (!sessionId) return;

      await updateResponseQueue({
        response_uid: data.response_uid,
        is_queued: false,
        is_packet: data.is_packet,
      });

      const baseUrl = url.split("/");
      const responseType = data.is_packet ? "p" : "f";
      const pushUrl = [
        ...baseUrl.slice(0, -1),
        responseType,
        sessionId,
        "client",
      ].join("/");
      history.push(pushUrl);
    },
    [history, setPacketSession, setResponseSession, updateResponseQueue, url],
  );

  const onSectionPress = React.useCallback(
    (data: ResponseQueue) => {
      setAlertConfig({
        title: data.is_packet ? "Start Packet" : "Start Form",
        description: `Are you sure you want to start the ${
          data.is_packet ? "packet" : "form"
        }?`,
        buttons: [
          { title: "Not now", onPress: () => {} },
          {
            title: "Start",
            style: "primary",
            onPress: () => startSession(data),
          },
        ],
        canDismiss: true,
      });
    },
    [setAlertConfig, startSession],
  );

  return (
    <>
      <Helmet>
        <title>Queue/Zentake</title>
      </Helmet>

      <div className="bg-screen h-screen w-screen flex justify-center">
        <div className="pt-6 md:px-3 max-w-screen-xl flex flex-col flex-1 h-full">
          <div className="max-w-screen-xl">
            <BreadcrumbButton onClick={() => history.goBack()}>
              Back
            </BreadcrumbButton>
            <div className="mb-4 lg:mb-0 w-full text-center">
              <Title>Queue</Title>
            </div>
          </div>
          {isFilledArray(responseQueue) ? (
            <div className="mt-8 mb-20 px-3">
              <p className="mb-3 text-sm text-txt">
                Select a client bellow to start
              </p>

              <div className="min-w-full sm:rounded border border-gray-400 mb-10 overflow-auto">
                <table className="table-auto min-w-full bg-white">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <Ui.TableTitle>CLIENT</Ui.TableTitle>
                      <Ui.TableTitle>FORM</Ui.TableTitle>
                      <Ui.TableTitle>STARTED ON</Ui.TableTitle>
                      <Ui.TableTitle />
                    </tr>
                  </thead>
                  <tbody>
                    {map(responseQueue, (response) => (
                      <tr
                        className="cursor-pointer hover:bg-gray-100"
                        key={response.response_uid}
                        onClick={() => onSectionPress(response)}
                      >
                        <Ui.TableItem>
                          {buildFullName(
                            response?.customer?.first_name,
                            response?.customer?.last_name,
                          )}
                        </Ui.TableItem>
                        <Ui.TableItem light>{response.form_name}</Ui.TableItem>
                        <Ui.TableItem light>
                          {getFormattedDate(response.created_at)}
                        </Ui.TableItem>
                        <Ui.TableItem>
                          <div className="flex justify-end">
                            <div onClick={(e) => onDeletePress(e, response)}>
                              <Icons.X className="cursor-pointer hover:text-txt-emphasis" />
                            </div>
                          </div>
                        </Ui.TableItem>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex justify-center text-center text-txt">
              No session available
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default TabletModeQueue;
